import {
    DeliveryPromiseOfferComparisonWithState,
    DeliveryPromiseTime,
    MerchantBusinessHours,
    ObfuscatedMerchantIdType,
    OffsetTimeType,
    OperatingStatus
} from "state/types"

/**
 * Return true if clinic is open, otherwise false.
 * Note: this method also return true if given merchantId not found from deliveryPromises
 */
const checkIfClinicOpen = (merchantId: ObfuscatedMerchantIdType, deliveryPromises: DeliveryPromiseTime[]): boolean => {
    return deliveryPromises.find(mdp => mdp.obfuscatedMerchantId === merchantId)?.operatingStatus?.isClinicOpen ?? true
}

/**
 * Compute the "OperatingStatus" given current time "now" and return a new copy of state
 */
const refreshProviderAvailableStatus = (state: DeliveryPromiseOfferComparisonWithState, now: Date = new Date()): DeliveryPromiseOfferComparisonWithState => {
    const updatedState: DeliveryPromiseOfferComparisonWithState = {
        ...state,
        variationDeliveryPromises: state.variationDeliveryPromises?.map(variationDeliveryPromise => ({
            ...variationDeliveryPromise,
            merchantDeliveryPromises: variationDeliveryPromise.merchantDeliveryPromises?.map(merchantDeliveryPromise => {
                let operatingStatus = merchantDeliveryPromise.businessHours ? DeliveryPromiseUtils.buildOperatingStatus(now, merchantDeliveryPromise.businessHours) : undefined
                let updatedMerchantDeliveryPromise: DeliveryPromiseTime = { ...merchantDeliveryPromise, operatingStatus: operatingStatus }
                return updatedMerchantDeliveryPromise;
            })
        }))
    }
    return updatedState
}

/**
 * Get operation status
 * @returns ProviderOperatingStatus or undefined if businessHours is invalid.
 */
const buildOperatingStatus = (now: Date, businessHours: MerchantBusinessHours): (OperatingStatus | undefined) => {
    if (!businessHours.operatingStrategies.includes('NOT_AVAILABLE_DURING_NON_BUSINESS_HOURS')) {
        // If provider is still open during non-business hour, do nothing.
        return;
    }
    const getMinuteSinceUTCDay = (date: Date) => {
        return date.getUTCHours() * 60 + date.getUTCMinutes()
    }
    const nowISODatePartition = now.toISOString().split('T')[0]
    const startOffsetDate = new Date(`${nowISODatePartition}T${businessHours.startOffsetTime}`)
    const endOffsetDate = new Date(`${nowISODatePartition}T${businessHours.endOffsetTime}`)
    if (isNaN(startOffsetDate.valueOf()) || isNaN(endOffsetDate.valueOf())) {
        return
    }
    const nowInMinutesSinceUTCDay = getMinuteSinceUTCDay(now)
    const startTimeInMinuteSinceUTCDay = getMinuteSinceUTCDay(startOffsetDate)
    const endTimeInMinuteSinceUTCDay = getMinuteSinceUTCDay(endOffsetDate)

    let isClinicOpen: boolean | undefined = undefined
    let toggleInMinute: number | undefined = undefined
    if (startTimeInMinuteSinceUTCDay == endTimeInMinuteSinceUTCDay) {
        // ideally this should never happen, if happen, we treat it as clinic is open 24 hours
        isClinicOpen = true;
        toggleInMinute = Number.MAX_SAFE_INTEGER
    } else if (startTimeInMinuteSinceUTCDay < endTimeInMinuteSinceUTCDay) {
        if (nowInMinutesSinceUTCDay >= startTimeInMinuteSinceUTCDay && nowInMinutesSinceUTCDay < endTimeInMinuteSinceUTCDay) {
            isClinicOpen = true;
            toggleInMinute = endTimeInMinuteSinceUTCDay - nowInMinutesSinceUTCDay
        } else {
            isClinicOpen = false;
            if (nowInMinutesSinceUTCDay < startTimeInMinuteSinceUTCDay) {
                toggleInMinute = startTimeInMinuteSinceUTCDay - nowInMinutesSinceUTCDay
            } else {
                toggleInMinute = startTimeInMinuteSinceUTCDay + (24 * 60 - nowInMinutesSinceUTCDay)
            }
        }
    } else if (startTimeInMinuteSinceUTCDay > endTimeInMinuteSinceUTCDay) {
        if (nowInMinutesSinceUTCDay < endTimeInMinuteSinceUTCDay) {
            isClinicOpen = true;
            toggleInMinute = endTimeInMinuteSinceUTCDay - nowInMinutesSinceUTCDay
        } else if (nowInMinutesSinceUTCDay >= startTimeInMinuteSinceUTCDay) {
            isClinicOpen = true;
            toggleInMinute = endTimeInMinuteSinceUTCDay + (24 * 60 - nowInMinutesSinceUTCDay)
        } else {
            isClinicOpen = false
            toggleInMinute = startTimeInMinuteSinceUTCDay - nowInMinutesSinceUTCDay
        }
    }
    return (isClinicOpen !== undefined && toggleInMinute !== undefined)
        ? { isClinicOpen, toggleInMinute }
        : undefined
}

/**
 * build response time display text for twister variation card
 */
const buildVariationResponseTimeText = (seconds: number) => {
    const secondsInt = Math.max(seconds, 0)
    const hour = Math.floor(secondsInt / 3600)
    const minute = Math.round((secondsInt % 3600) / 60)
    if (hour === 0 && minute === 0) {
        return "1 minute";
    } else {
        const hourText = hour === 0 ? '' : `${hour} hour${hour > 1 ? 's' : ''}`
        let minuteText = '';
        if (minute === 1) {
            minuteText = minute + ' minute'
        }else if (minute > 1) {
            minuteText = minute + ' mins'
        }
        return hourText + ' ' + minuteText;
    }
}

/**
 * build responset time display text for buying option
 */
const buildBuyingOptionResponseTimeText = (seconds: number) => {
    const secondsInt = Math.max(seconds, 0)
    const hour = Math.floor(secondsInt / 3600)
    const minute = Math.round((secondsInt % 3600) / 60)
    if (hour === 0 && minute === 0) {
        return "1 minute or less";
    } else {
        const hourText = hour === 0 ? '' : `${hour} hour${hour > 1 ? 's' : ''}`
        const minuteText = minute === 0 ? '' : `${minute} minute${minute > 1 ? 's' : ''}`
        const joiner = !!hourText && !!minuteText ? ' and ' : ''
        return hourText + joiner + minuteText + ' or less';
    }
}

/**
 * build business hour display text with local timezone.
 */
const buildBusinessHourLocalTime = (offsetTime: OffsetTimeType, isShowTimeZone: boolean): (String | undefined) => {
    const nowISODatePartition = new Date().toISOString().split('T')[0]
    const offsetDate = new Date(`${nowISODatePartition}T${offsetTime}`)
    if (isNaN(offsetDate.valueOf())) {
        return
    }
    const timeZoneFormat = new Intl.DateTimeFormat("en", {
        timeZoneName: "shortGeneric"
    });
    const timeZoneShortGeneric = timeZoneFormat.format(offsetDate).split(',').pop()?.trim()
    const timeFormat = new Intl.DateTimeFormat("en", {
        timeStyle: "short",
    });
    let time = timeFormat.format(offsetDate);
    if (offsetDate.getMinutes() === 0) {
        //remove :00 from time text
        time = time.replace(':00', '')
    }
    if (isShowTimeZone) {
        return `${time} ${timeZoneShortGeneric}`
    } else {
        return `${time}`
    }
}

const DeliveryPromiseUtils = {
    buildOperatingStatus,
    buildVariationResponseTimeText: buildVariationResponseTimeText,
    buildBuyingOptionResponseTimeText: buildBuyingOptionResponseTimeText,
    buildBusinessHourLocalTime,
    refreshProviderAvailableStatus,
    checkIfClinicOpen
}
export default DeliveryPromiseUtils